import { Chat as ChatType, ModelType } from '../types';

// 添付ファイル型定義
export interface Attachment {
  id: number;
  type: 'image' | 'video' | 'document';
  url: string;
  s3_url?: string;
  s3_key?: string;
  filename: string;
  mime_type: string;
  size: number;
  width?: number;
  height?: number;
  duration?: number;
}

// アップロード済みファイル型定義
export interface UploadedFile {
  file_id: string;
  service: string;
  name: string;
  mime_type?: string;
  s3_url?: string;
  s3_key?: string;
  s3_bucket?: string;
}

// 検索結果型定義
export interface SearchResult {
  query_id: string;
  rank: number;
  url: string;
  title: string;
  snippet: string;
  score?: number;
}

// 検索クエリ型定義
export interface SearchQuery {
  query_id: string;
  text: string;
  timestamp: string;
}

// 検索メタデータ型定義
export interface SearchMetadata {
  queries: SearchQuery[];
  results: SearchResult[];
  total_results: number;
  duration_ms: number;
  provider: string;
  completed_at: string;
  error?: string;
}

// ツールカードの表示種別（描画するカードの形を決める）
export type ToolCardKind =
  | 'info'
  | 'image'
  | 'video'
  | 'carousel'
  | 'proposal'
  | 'choices'
  // チャットv2 リニューアルのブロック（payload は ToolCallDisplay.block。描画は components/chat-v2/blocks/registry）
  | 'storyboard'
  | 'ugc_cast'
  | 'video_edit'
  | 'auto_run'
  | 'approval'
  | 'cuts'
  | 'material';

/** チャット入力の生成モード。選択中は LLM を通さず、本文から画像／動画を生成する。 */
export type ChatGenerationMode = 'image' | 'video';

// カード上に並べる「ラベル: 値」のチップ（例: 長さ: 8秒）
export interface ToolCardChip { label: string; value: string }

// カードに表示する画像
export interface ToolCardImage { id?: number; url: string }

// 動画カードの1本分（ジョブの進捗・完成した動画の情報）
export interface ToolCardVideo {
  job_id?: string | null;
  video_id?: number | null;
  status: 'pending' | 'processing' | 'completed' | 'failed' | 'cancelled' | string;
  progress_step?: string | null;
  progress_percentage?: number | null;
  video_model?: string | null;
  aspect_ratio?: string | null;
  duration_seconds?: number | null;
  resolution?: string | null;
  url?: string | null;
  prompt?: string | null;
  error_code?: string | null;
  error_message?: string | null;
  created_at?: string | null;
}

// カルーセルカードの1スライド分（画像生成ジョブへの参照）
export interface ToolCardJob { job_id: string; slide_index: number; role?: string | null; status?: string | null }

// 実行前確認カード（ユーザーが確認コードを返すまで生成しない）
export interface ToolCardProposal {
  code: string;
  tool: string;
  label: string;
  chips: ToolCardChip[];
  estimated_credits: number;
  expires_at: string;
}

// 選択肢カード（ask_user_choices）の1項目。recommended=true が先頭に並ぶ（backend で並べ替え済み）
// 選択肢の見本（サーバーが media_ref を組織スコープで署名付き URL に解決したもの）
export interface ToolCardChoiceMedia {
  type: 'image' | 'video' | 'audio';
  src: string;
  poster?: string | null;
}

export interface ToolCardChoiceOption {
  id: string;
  label: string;
  /** 補足（仕様書の sub） */
  description?: string | null;
  recommended: boolean;
  media_url?: string | null;
  media_type?: 'image' | 'video' | null;
  media_ref?: string | null;
  media?: ToolCardChoiceMedia | null;
  /** 選んだときのクレジット。0 は無料、null/undefined は表示しない */
  cost?: number | null;
}

// 選択肢カード（チャットv2）。選ばれた label を通常のユーザーメッセージとして送る
export interface ToolCardChoices {
  question: string;
  options: ToolCardChoiceOption[];
  allow_free_text: boolean;
  multi_select: boolean;
  /** カード内の「自分で入力」欄。null なら出さない（はい／いいえ型） */
  custom?: { placeholder: string } | null;
}

// チャットツール実行の表示用型（tool_use / tool_result を call_id で集約したもの）
export interface ToolCallDisplay {
  call_id: string;
  name: string;
  label: string; // 日本語表示名（例: バナー生成）
  args_summary?: string | null;
  status: 'running' | 'progress' | 'completed' | 'error';
  progress?: number | null;
  summary?: string | null;
  job_id?: string | null;
  images?: ToolCardImage[];
  ok?: boolean; // サーバー永続化レコード（messages.tool_calls）由来。status 未設定時の判定に使う
  kind?: ToolCardKind;
  videos?: ToolCardVideo[];
  jobs?: ToolCardJob[];
  proposal?: ToolCardProposal | null;
  chips?: ToolCardChip[];
  proposal_code?: string | null;
  choices?: ToolCardChoices | null;
  /** チャットv2 の新しいブロック種別の payload（kind ごとに形が決まる。_docs/chat-v2/renewal-design.md） */
  block?: Record<string, unknown> | null;
  completed_at?: string | null;
}

// メッセージ型定義
export interface Message {
  id: number;
  content: string;
  sender_type: 'user' | 'ai';
  created_at: string;
  status?: 'pending' | 'processing' | 'completed' | 'failed'; // メッセージステータス
  animation_complete?: boolean; // アニメーション完了フラグ
  attachments?: Attachment[]; // 添付ファイル配列
  suggestions?: string[]; // AIからの返答サジェスト
  uploaded_files?: UploadedFile[]; // アップロード済みファイル情報
  search_metadata?: SearchMetadata | null; // 検索メタデータ
  tool_calls?: ToolCallDisplay[]; // チャットツール実行履歴（カード表示・画像表示用）
  auto_run_id?: number | null; // チャットv2 オートで進めたターン（⚡オート表示）
}

// グローバルイベント名の定数
export const ANIMATION_COMPLETE_EVENT = 'chat-animation-complete';

// 簡易的なチャット型定義（ローカル用）
export interface Chat {
  id: number;
  title: string;
  created_at: string;
  updated_at: string;
}

// エージェント型定義
export interface AgentWithIcon {
  id: number;
  title: string;
  description?: string;
  model_type: ModelType;
  prompt_template: string;
  is_active: boolean;
  is_visible_in_sidebar?: boolean;  // サイドバーに表示するかどうか
  created_at?: string;
  updated_at?: string;
  icon: string;
  name: string;
  avatar: string;
  capabilities: string[];
  base_color?: string;
  category?: string;
  quick_replies?: string[];  // 初回表示用のクイックリプライ（最大4つ）
}

// フォーマット済みチャット情報（Sidebar用）
export interface FormattedChat {
  id: number;
  title: string;
}

// フォーマット済みエージェント情報（Sidebar用）
export interface FormattedAgent {
  id: number;
  name: string;
  description: string;
  avatar: string;
  capabilities: string[];
}

// 確認ダイアログのアクション種別
export type PendingActionType = 'new' | 'existing' | null;
